import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { CursorCandles } from "@/components/CursorCandles";
import { Crosshair } from "@/components/Crosshair";
import { Reveal } from "@/components/Reveal";
import { TerminalReadout } from "@/components/TerminalReadout";
import { ProfileCard } from "@/components/rank/ProfileCard";
import { ErrorPanel, LoadingPanel } from "@/components/rank/States";
import { fetchRankProfile, searchUsers } from "@/lib/api";
import type { RankProfile, RankUser } from "@/lib/types";

export const Route = createFileRoute("/app")({
  head: () => ({
    meta: [
      { title: "Hoodrank App — Score any X handle by its followers" },
      {
        name: "description",
        content:
          "Paste any X handle and get its Hoodrank score, key followers and tier. Reputation scoring for the Robinhood chain ecosystem.",
      },
      { property: "og:title", content: "Hoodrank App — Check any X handle" },
      {
        property: "og:description",
        content:
          "Paste a handle, get the rank: score, tier and the key followers driving it.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AppScreen,
});

type State =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; profile: RankProfile };

const IDLE_STEPS = [
  {
    n: "01",
    title: "Paste a handle",
    body: "Any @username — yours, a competitor, or a viral account.",
  },
  {
    n: "02",
    title: "Read the graph",
    body: "We weight who follows them: KOLs, projects, funds — not raw count.",
  },
  {
    n: "03",
    title: "Get the rank",
    body: "One score, tier, and the key followers driving it.",
  },
] as const;

function AppScreen() {
  const [query, setQuery] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const [lastQuery, setLastQuery] = useState("");
  const [suggestions, setSuggestions] = useState<RankUser[]>([]);
  const [openSuggest, setOpenSuggest] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchGen = useRef(0);

  const run = async (value: string) => {
    searchGen.current += 1; // ignore in-flight autocomplete
    setLastQuery(value);
    setOpenSuggest(false);
    setSuggestions([]);
    setState({ kind: "loading" });
    try {
      const profile = await fetchRankProfile(value);
      setState({ kind: "ready", profile });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : "Unknown error",
      });
    }
  };

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim().replace(/^@/, "");
    if (q.length < 2) {
      setSuggestions([]);
      setOpenSuggest(false);
      return;
    }
    const gen = ++searchGen.current;
    debounce.current = setTimeout(() => {
      void searchUsers(q).then((users) => {
        if (gen !== searchGen.current) return;
        const list = users.slice(0, 6);
        setSuggestions(list);
        // Only open while the user is still typing in the field — never after submit.
        const focused = inputRef.current === document.activeElement;
        setOpenSuggest(focused && list.length > 0);
      });
    }, 280);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpenSuggest(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Tier drives the accent token on <body>.
  useEffect(() => {
    if (state.kind !== "ready") return;
    document.body.dataset.tier = state.profile.tier;
    return () => {
      document.body.dataset.tier = "elite";
    };
  }, [state]);

  return (
    <div className="flex min-h-dvh flex-col">
      <CursorCandles />
      <Crosshair />

      <header className="sticky top-0 z-50 shrink-0 border-b border-line bg-bg">
        <div className="shell flex h-16 items-center justify-between gap-4 md:h-18">
          <Link
            to="/"
            className="flex items-center text-[20px] leading-none font-display tracking-tight"
          >
            <span className="text-ink">HOOD</span>
            <span className="ml-1 bg-accent px-1.5 py-0.5 text-accent-ink">
              RANK
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/trending" className="navlink hidden py-1 sm:block">
              Trending
            </Link>
            <Link to="/" className="navlink py-1">
              ← Back to site
            </Link>
          </div>
        </div>
      </header>

      <main className="grid-paper flex-1 overflow-x-clip">
        <div className="shell py-10 md:py-14">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] lg:gap-12">
            <div>
              <Reveal>
                <p className="label mb-6">Terminal</p>
              </Reveal>
              <Reveal delay={90}>
                <h1 className="max-w-[16ch] !text-[clamp(40px,6vw,84px)]">
                  Check any <span className="hollow">handle</span>
                </h1>
              </Reveal>

              <Reveal delay={180}>
                <form
                  className="mt-10 flex max-w-[640px] flex-col gap-3 sm:flex-row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void run(query);
                  }}
                >
                  <div ref={wrapRef} className="relative min-w-0 flex-1">
                    <label className="sr-only" htmlFor="handle">
                      X handle
                    </label>
                    <input
                      id="handle"
                      ref={inputRef}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() =>
                        suggestions.length > 0 && setOpenSuggest(true)
                      }
                      placeholder="@username"
                      autoComplete="off"
                      className="mono w-full border border-ink bg-bg px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted"
                    />
                    {openSuggest ? (
                      <ul
                        role="listbox"
                        className="absolute inset-x-0 top-full z-20 border border-ink border-t-0 bg-bg-panel shadow-none"
                      >
                        {suggestions.map((u) => (
                          <li key={u.id}>
                            <button
                              type="button"
                              role="option"
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-bg"
                              onClick={() => {
                                setQuery(`@${u.username}`);
                                void run(u.username);
                              }}
                            >
                              <span className="mono text-[13px] font-semibold text-ink">
                                @{u.username}
                              </span>
                              <span className="truncate text-[12px] text-ink-muted">
                                {u.display_name}
                                {u.verified ? " ✓" : ""}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  <button
                    type="submit"
                    disabled={state.kind === "loading"}
                    className="btn btn-dark disabled:opacity-60"
                  >
                    {state.kind === "loading" ? "Scoring…" : "Get rank →"}
                  </button>
                </form>
              </Reveal>

              <Reveal delay={260}>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="label">Try</span>
                  {["@vladtenev", "@RobinhoodCrypto", "@ponsdotfamily"].map(
                    (h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => {
                          setQuery(h);
                          void run(h);
                        }}
                        className="mono border border-line px-2 py-1 text-[12px] text-ink-soft hover:border-ink hover:text-ink"
                      >
                        {h}
                      </button>
                    ),
                  )}
                </div>
              </Reveal>
            </div>

            <Reveal delay={140}>
              <TerminalReadout
                phase={state.kind}
                profile={state.kind === "ready" ? state.profile : null}
                handle={
                  state.kind === "loading" || state.kind === "error"
                    ? lastQuery
                    : state.kind === "ready"
                      ? state.profile.user.username
                      : query
                }
              />
            </Reveal>
          </div>

          <div className="mt-12">
            {state.kind === "loading" ? (
              <Reveal>
                <LoadingPanel />
              </Reveal>
            ) : null}
            {state.kind === "error" ? (
              <ErrorPanel
                message={state.message}
                onRetry={() => void run(lastQuery)}
              />
            ) : null}
            {state.kind === "ready" ? (
              <Reveal key={state.profile.user.username} delay={40}>
                <ProfileCard profile={state.profile} />
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/profile/$handle"
                    params={{ handle: state.profile.user.username }}
                    className="btn btn-dark"
                  >
                    Full profile →
                  </Link>
                  <Link
                    to="/feed/$handle"
                    params={{ handle: state.profile.user.username }}
                    className="btn btn-outline"
                  >
                    Posts &amp; mentions
                  </Link>
                </div>
              </Reveal>
            ) : null}

            {state.kind === "idle" ? (
              <Reveal delay={300}>
                <section className="border border-ink bg-bg-panel">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-3">
                    <p className="label">How it works</p>
                    <p className="mono text-[11px] text-ink-muted">
                      three moves · then score
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3">
                    {IDLE_STEPS.map((s, i) => (
                      <div
                        key={s.n}
                        className={`p-5 ${i > 0 ? "border-t border-line md:border-t-0 md:border-l" : ""}`}
                      >
                        <p className="font-display text-[42px] leading-none text-accent">
                          {s.n}
                        </p>
                        <p className="mono mt-4 text-[13px] font-semibold text-ink">
                          {s.title}
                        </p>
                        <p className="mono mt-2 text-[12px] leading-relaxed text-ink-muted">
                          {s.body}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              </Reveal>
            ) : null}
          </div>

          <p className="mono mt-6 text-[12px] text-ink-muted">
            Scores are reputation points from the indexed follower graph — not a
            0–3 scale.
          </p>
        </div>
      </main>
    </div>
  );
}
